import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FlaskConical, Syringe, Pill, HeartPulse, Smartphone, CheckCircle2, ArrowRight, Sparkles, Flame, RotateCcw } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { BIG365, IMG, CHALKIT_URL, SITE, chalkitLink } from "../data/mock";
import { LabsCards } from "../components/LabsCards";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pillarIcons = [FlaskConical, Syringe, Pill, HeartPulse];

const todayKey = () => new Date().toISOString().slice(0, 10);
const yesterdayKey = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Interactive one-day habit checklist demo (persists in the visitor's browser)
function useHabitChecklist() {
  const [checked, setChecked] = useState([]);
  const [streak, setStreak] = useState(0);
  const [lastPerfect, setLastPerfect] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("ig_big365_demo") || "{}");
      if (saved.date === todayKey() && Array.isArray(saved.checked)) setChecked(saved.checked);
      setStreak(saved.streak || 0);
      setLastPerfect(saved.lastPerfect || null);
    } catch (e) {
      /* fresh start */
    }
  }, []);

  const persist = (nextChecked, nextStreak, nextLastPerfect) => {
    localStorage.setItem(
      "ig_big365_demo",
      JSON.stringify({ date: todayKey(), checked: nextChecked, streak: nextStreak, lastPerfect: nextLastPerfect })
    );
  };

  const toggle = (num) => {
    const next = checked.includes(num) ? checked.filter((n) => n !== num) : [...checked, num];
    let nextStreak = streak;
    let nextLast = lastPerfect;
    if (next.length === BIG365.habits.length && lastPerfect !== todayKey()) {
      nextStreak = lastPerfect === yesterdayKey() ? streak + 1 : 1;
      nextLast = todayKey();
      setStreak(nextStreak);
      setLastPerfect(nextLast);
    }
    setChecked(next);
    persist(next, nextStreak, nextLast);
  };

  const reset = () => {
    setChecked([]);
    persist([], streak, lastPerfect);
  };

  return { checked, streak, toggle, reset };
}

export default function LiveBig365() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [sending, setSending] = useState(false);
  const [ch, setCh] = useState({ name: "", email: "", phone: "" });
  const [chDone, setChDone] = useState(false);
  const [chSending, setChSending] = useState(false);
  const [mountTs] = useState(() => Date.now());
  const { checked, streak, toggle, reset } = useHabitChecklist();
  const allDone = checked.length === BIG365.habits.length;

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/waitlist`, { email, source: "live-big-365" });
      axios.post(`${API}/subscribe`, { email, source: "app-waitlist", tags: ["app-waitlist"], consent: true, ts: mountTs }).catch(() => {});
      setJoined(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: "We couldn't add you to the waitlist. Please try again." });
    } finally {
      setSending(false);
    }
  };

  const joinChallenge = async (e) => {
    e.preventDefault();
    setChSending(true);
    try {
      await axios.post(`${API}/challenge`, ch);
      axios.post(`${API}/subscribe`, {
        email: ch.email,
        firstName: ch.name.split(" ")[0],
        lastName: ch.name.split(" ").slice(1).join(" "),
        phone: ch.phone,
        source: "challenge-8week",
        tags: ["8-week-challenge"],
        consent: true,
        ts: mountTs,
      }).catch(() => {});
      if (typeof window.gtag === "function") {
        window.gtag("event", "challenge_signup", { form: "8_week_challenge" });
      }
      setChDone(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: "We couldn't save your signup. Please try again." });
    } finally {
      setChSending(false);
    }
  };

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Live B.I.G. 365", path: "/live-big-365" },
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Live B.I.G. 365 — Wellness & Longevity Program",
    serviceType: "Wellness and Longevity Program",
    description: BIG365.answer,
    provider: { "@id": "https://bostonirongrittraining.com/#business" },
    areaServed: { "@type": "Place", name: "West Roxbury, Boston, MA" },
    url: "https://bostonirongrittraining.com/live-big-365",
    offers: { "@type": "Offer", price: "100", priceCurrency: "USD", description: "$100/month, included free with the Platinum Membership" },
  };

  return (
    <>
      <Seo
        title="Live B.I.G. 365 Longevity Program | Boston Iron Grit"
        description="Live B.I.G. 365 is Boston Iron Grit's longevity program: 8 daily habits, labs, body composition, peptides and GLP-1s via licensed providers. $100/month."
        path="/live-big-365"
        schemas={[breadcrumbSchema(crumbs), serviceSchema, faqSchema(BIG365.faqs)]}
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.chalk})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]/40" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <Reveal>
            <Crumbs items={crumbs} />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// Wellness & Longevity · 5+ Years in the Making</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-5xl sm:text-6xl lg:text-[80px] text-white leading-[0.92] tracking-tight">
              Live B.I.G.<br /><span className="text-[#9D0423]">365.</span>
            </h1>
            <p className="mt-7 text-neutral-200 text-base lg:text-lg leading-relaxed max-w-2xl border-l-2 border-[#9D0423] pl-5">
              {BIG365.answer}
            </p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500" data-testid="big365-creator">
              Created by Owner & Head Coach <span className="text-[#E56A79]">{BIG365.creator}</span> · refined over 5+ years
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnPrimary href={chalkitLink("big365")}>Join Live B.I.G. 365</BtnPrimary>
              <BtnGhost to="/get-started">Start With a Free Goal Review</BtnGhost>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400">
              <span>8 daily habits</span><span className="text-neutral-700">/</span>
              <span>Labs & bloodwork</span><span className="text-neutral-700">/</span>
              <span>Peptides & GLP-1s</span><span className="text-neutral-700">/</span>
              <span>$100/mo · free with Platinum</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8 DAILY HABITS */}
      <section id="habits" className="bg-[#0A0A0B] py-20 lg:py-28 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The 8 Daily Habits</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Small habits.<br /><span className="text-neutral-600">Every single day.</span>
            </h2>
            <p className="mt-5 text-neutral-400 text-[15px] max-w-2xl">
              Longevity isn't built in one workout — it's developed with daily habits that help you look better and
              feel your best. Our 8 daily habits are combined into three areas to unlock your avidity, vitality and
              potential — with an app in development to help you track them.
            </p>
            <p className="mt-3 text-neutral-500 text-[14px] max-w-2xl">{BIG365.challenge}</p>
          </Reveal>
          <div className="mt-12 space-y-10">
            {BIG365.areas.map((area, ai) => (
              <div key={area.name}>
                <Reveal>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-5 mb-4">
                    <h3 className="font-heading font-extrabold uppercase text-2xl text-[#9D0423] tracking-tight">
                      {String(ai + 1).padStart(2, "0")} · {area.name}
                    </h3>
                    <p className="text-[13px] text-neutral-500">{area.blurb}</p>
                  </div>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
                  {BIG365.habits.filter((h) => h.area === area.name).map((h, i) => (
                    <Reveal key={h.num} delay={i * 70}>
                      <div className="group bg-[#0D0D0F] p-7 h-full hover:bg-[#121214] transition-colors relative overflow-hidden">
                        <div className="absolute -top-3 -right-2 font-heading font-extrabold text-[80px] leading-none text-white/[0.04] group-hover:text-[#9D0423]/10 transition-colors select-none">{h.num}</div>
                        <div className="relative">
                          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9D0423]">Habit {h.num}</div>
                          <h4 className="mt-3 font-heading font-bold uppercase text-xl text-white tracking-tight">{h.name}</h4>
                          <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{h.detail}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                  {area.name === "Train" && (
                    <Reveal delay={160}>
                      <blockquote data-speakable="true" data-testid="big365-wall-quote" className="bg-[#0D0D0F] p-7 h-full flex flex-col justify-center border-l-2 border-[#9D0423]">
                        <p className="font-heading font-bold uppercase text-xl text-white tracking-tight leading-snug">“What would you attempt to do if you knew you could not fail?”</p>
                        <cite className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 not-italic">On the wall at Boston Iron Grit</cite>
                      </blockquote>
                    </Reveal>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>What's Inside</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              The full toolbox
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {BIG365.pillars.map((p, i) => {
              const Icon = pillarIcons[i];
              return (
                <Reveal key={p.name} delay={(i % 2) * 90}>
                  <div className="p-8 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                    <div className="flex items-center justify-between">
                      <Icon size={24} className="text-[#9D0423]" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E56A79] bg-[#9D0423]/10 border border-[#9D0423]/25 px-2.5 py-1">{p.tag}</span>
                    </div>
                    <h3 className="mt-5 font-heading font-bold uppercase text-2xl text-white tracking-tight">{p.name}</h3>
                    <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{p.detail}</p>
                    <ul className="mt-5 space-y-2.5">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2.5">
                          <CheckCircle2 size={15} className="text-[#9D0423] mt-0.5 shrink-0" />
                          <span className="text-[13px] text-neutral-300">{pt}</span>
                        </li>
                      ))}
                    </ul>
                    {p.name === "Peptides & GLP-1s" && (
                      <Link to="/peptides-glp1-boston" data-testid="big365-peptides-link" className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#E56A79] hover:text-white transition-colors">
                        Learn how it works <ArrowRight size={13} />
                      </Link>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-8 text-[12px] text-neutral-600 leading-relaxed max-w-3xl">
            Peptides, GLP-1s and lab panels are provided through licensed medical partners; a provider consultation and
            eligibility screening are required, and these services are billed separately. Live B.I.G. 365 coaching is not
            medical advice — always consult your physician about medical decisions.
          </p>
          <Reveal>
            <div className="mt-10 p-7 bg-[#121214] border border-[#9D0423]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] mb-2">Take it to the next level</p>
                <p className="text-[15px] text-neutral-200 leading-relaxed max-w-2xl">
                  Interested in peptides and GLP-1s? Our <span className="text-white font-semibold">Gold Standard Programs</span> —
                  The Foundation, The Ascension and The Elite — include personal training and labs, and are the best place to start.
                </p>
              </div>
              <Link
                to="/pricing#gold-standard"
                className="shrink-0 inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
              >
                Gold Standard Programs <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LABS */}
      <section className="bg-[#0D0D0F] py-16 lg:py-20 border-y border-white/5" data-testid="big365-labs">
        <div className="max-w-7xl mx-auto px-6">
          <LabsCards idPrefix="big365" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              Test. Train. Tune.<br /><span className="text-neutral-600">Repeat for life.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {BIG365.steps.map((s, i) => (
              <Reveal key={s.name} delay={i * 70}>
                <div className="bg-[#0D0D0F] p-6 h-full hover:bg-[#121214] transition-colors">
                  <div className="font-heading font-extrabold text-4xl text-white/10">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-3 font-heading font-bold uppercase text-base text-white tracking-tight leading-snug">{s.name}</h3>
                  <p className="mt-2 text-[12px] text-neutral-400 leading-relaxed">{s.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8-WEEK CHALLENGE */}
      <section id="challenge" className="bg-[#0A0A0B] py-20 lg:py-28 scroll-mt-24" data-testid="challenge-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] bg-[#9D0423]/10 border border-[#9D0423]/25 px-3 py-1.5 mb-6">
              <Flame size={12} /> Next cohort announced soon
            </div>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              The 8-Week<br /><span className="text-[#9D0423]">Challenge.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed max-w-lg">
              {BIG365.challenge} Eight weeks of the 8 Daily Habits with your gym in your corner — the fastest way to
              turn good intentions into a routine that sticks.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Daily habit tracking with the group",
                "Coach check-ins and a friendly leaderboard",
                "InBody body composition scans before & after",
                "Open to members and non-members",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-300">
                  <CheckCircle2 size={17} className="text-[#9D0423] mt-0.5 shrink-0" /> {li}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="bg-[#121214] border border-white/10 p-8 lg:p-10">
              {chDone ? (
                <div className="py-6" data-testid="challenge-success">
                  <CheckCircle2 size={36} className="text-[#9D0423]" />
                  <h3 className="mt-5 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">You're on the roster, {ch.name.split(" ")[0]}!</h3>
                  <p className="mt-3 text-neutral-300 text-[15px] leading-relaxed">
                    We'll reach out the moment the next 8-week cohort opens with dates and everything you need to start.
                  </p>
                </div>
              ) : (
                <>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// Save your spot</p>
                  <h3 className="mt-3 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">Join the next cohort</h3>
                  <form onSubmit={joinChallenge} className="mt-6 space-y-4">
                    <input
                      required
                      value={ch.name}
                      onChange={(e) => setCh({ ...ch, name: e.target.value })}
                      placeholder="Your name"
                      aria-label="Name for challenge signup"
                      data-testid="challenge-name"
                      className="w-full bg-[#0A0A0B] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                    />
                    <input
                      type="email"
                      required
                      value={ch.email}
                      onChange={(e) => setCh({ ...ch, email: e.target.value })}
                      placeholder="you@email.com"
                      aria-label="Email for challenge signup"
                      data-testid="challenge-email"
                      className="w-full bg-[#0A0A0B] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                    />
                    <input
                      type="tel"
                      value={ch.phone}
                      onChange={(e) => setCh({ ...ch, phone: e.target.value })}
                      placeholder="Phone (optional)"
                      aria-label="Phone for challenge signup"
                      data-testid="challenge-phone"
                      className="w-full bg-[#0A0A0B] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={chSending}
                      data-testid="challenge-submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-6 py-4 transition-colors"
                    >
                      {chSending ? "Saving…" : "Save My Spot"} <ArrowRight size={15} />
                    </button>
                    <p className="text-[11px] text-neutral-600 leading-relaxed">
                      No commitment — we'll contact you with cohort dates and details. We never share your info.
                    </p>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* APP WAITLIST */}
      <section className="relative bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] bg-[#9D0423]/10 border border-[#9D0423]/25 px-3 py-1.5 mb-6">
              <Sparkles size={12} /> Coming Soon
            </div>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              The Live B.I.G. 365<br /><span className="text-[#9D0423]">app.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed max-w-lg">
              Daily 8-habit challenges, journals, nutrition advice and progress tracking — built to support every step of
              your health journey. In development now. Join the waitlist and be first in.
            </p>
            {joined ? (
              <div className="mt-8 flex items-center gap-3 p-5 bg-[#121214] border border-[#9D0423]/40 max-w-md">
                <CheckCircle2 size={20} className="text-[#9D0423] shrink-0" />
                <p className="text-sm text-neutral-200">You're on the list! We'll email you the moment the app drops.</p>
              </div>
            ) : (
              <form onSubmit={joinWaitlist} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  aria-label="Email for app waitlist"
                  data-testid="waitlist-email"
                  className="flex-1 bg-[#0A0A0B] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="waitlist-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
                >
                  {sending ? "Joining…" : "Join Waitlist"} <ArrowRight size={14} />
                </button>
              </form>
            )}
            <p className="mt-4 text-[11px] text-neutral-600">No spam — one email when we launch, that's it.</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative mx-auto w-full max-w-sm">
              <div className="bg-[#121214] border border-white/10 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                <div className="flex items-center justify-between pb-5 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <Smartphone size={18} className="text-[#9D0423]" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400" data-testid="habit-progress-label">
                      Today's Habits · {checked.length}/{BIG365.habits.length}
                    </span>
                  </div>
                  <button
                    onClick={reset}
                    aria-label="Reset today's checklist"
                    data-testid="habit-reset"
                    className="p-1.5 text-neutral-600 hover:text-white transition-colors"
                  >
                    <RotateCcw size={14} />
                  </button>
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#E56A79]">
                  Try it now — tap to check off your day
                </p>
                <div className="mt-4 space-y-2.5">
                  {BIG365.habits.map((h) => {
                    const done = checked.includes(h.num);
                    return (
                      <button
                        key={h.num}
                        onClick={() => toggle(h.num)}
                        data-testid={`habit-${h.num}`}
                        aria-pressed={done}
                        className={`w-full flex items-center justify-between p-3 border text-left transition-all duration-200 ${
                          done
                            ? "bg-[#9D0423]/10 border-[#9D0423]/40"
                            : "bg-[#0D0D0F] border-white/5 hover:border-white/20"
                        }`}
                      >
                        <span className={`text-[13px] font-medium transition-colors ${done ? "text-white" : "text-neutral-300"}`}>
                          {h.name}
                        </span>
                        {done ? (
                          <CheckCircle2 size={16} className="text-[#9D0423]" />
                        ) : (
                          <span className="w-4 h-4 border border-white/20 rounded-full" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-5 h-1.5 bg-white/5">
                  <div
                    className="h-full bg-[#9D0423] transition-all duration-500"
                    style={{ width: `${(checked.length / BIG365.habits.length) * 100}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5" data-testid="habit-streak">
                    <Flame size={12} className={streak > 0 ? "text-[#9D0423]" : "text-neutral-600"} />
                    {streak > 0 ? `Streak: ${streak} day${streak > 1 ? "s" : ""}` : "Start your streak today"}
                  </p>
                  {allDone && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#E56A79]" data-testid="habit-perfect">
                      A B.I.G. Day — Live B.I.G.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICING STRIP */}
      <section className="bg-[#0A0A0B] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Simple Pricing</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-10">Two ways in</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            <Reveal>
              <div className="p-8 bg-[#121214] border border-white/8 h-full">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">Standalone</p>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-heading font-extrabold text-5xl text-white">{BIG365.price}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">{BIG365.per}</span>
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">Full Live B.I.G. 365 program — no gym membership required.</p>
                <a href={chalkitLink("big365")} target="_blank" rel="noopener noreferrer" data-testid="big365-standalone-cta" className="mt-6 inline-flex items-center justify-center w-full border border-white/20 hover:border-white/60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors">
                  Join for $100/mo
                </a>
              </div>
            </Reveal>
            <Reveal delay={90}>
              <div className="p-8 bg-[#141416] border-2 border-[#9D0423] h-full">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#E56A79]">Best Value</p>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-heading font-extrabold text-5xl text-white">FREE</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">with Unlimited · $275/mo</span>
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">Unlimited classes + the full wellness program, bundled into one membership.</p>
                <Link to="/pricing" className="mt-6 inline-flex items-center justify-center w-full bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors">
                  See Unlimited Membership
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={BIG365.faqs} title="Live B.I.G. 365 — FAQs" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Ready to live B.I.G.?"
        sub="Book a free goal review and we'll map your baseline — labs, InBody and the 8 habits — into a plan you can run for decades."
      />
    </>
  );
}
